import type { OrderedJSONSchema } from "@elephant-healthcare/yupify";
import React, { Suspense } from "react";
import type { SchemaDescription } from "yup/lib/schema";
import { DynamicFieldsContext } from "./dynamic-form-fields-context";
import LoadingField from "./loading-field";

function isReactComponent(componentName: string): boolean {
  const [firstLetter] = componentName;
  return firstLetter.toUpperCase() === firstLetter;
}

interface LazyFieldProps {
  component: string;
  describedFieldSchema?: SchemaDescription;
  fieldDef: OrderedJSONSchema;
  name: string;
  required?: boolean;
  disabled?: boolean;
  type: string;
}

// Used to load fields at runtime,
// else everything in (non-lazy) `fieldMap` is included in the bundle
const LazyField = ({
  component,
  describedFieldSchema,
  fieldDef,
  type,
  ...props
}: LazyFieldProps): React.ReactElement => {
  const { lazyFieldMap: fieldMap } = React.useContext(DynamicFieldsContext);
  const { label } = describedFieldSchema;
  const allPropsToPass = {
    ...props,
    type,
    label,
  };

  if (!isReactComponent(component)) {
    const Component = component as unknown as FC<
      React.HTMLProps<HTMLInputElement>
    >;
    return <Component {...allPropsToPass} />;
  }

  const Component = fieldMap[component];
  if (!Component) {
    console.error(`Lazy component not found: ${component}`);
    return null;
  }

  return (
    <Suspense fallback={<LoadingField />}>
      <Component
        {...allPropsToPass}
        describedFieldSchema={describedFieldSchema}
        fieldDef={fieldDef}
      />
    </Suspense>
  );
};

LazyField.defaultProps = {
  describedFieldSchema: {},
  required: false,
  disabled: false,
};

export default LazyField;
